/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../components/banner";
import Newsletter from "../components/newsletter";
import OpenGraphHead from "../components/og-head";
import TwitterHead from "../components/twitter-head";
import { toAbsolutePageURL } from "../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class Planet extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/planet", market);
    const alt = "Lasting Smiles";
    const title = "Planet · Lasting Smiles";
    const description = "We are committed to creating the best lip balm and lip care products with all natural, organic, and fair trade ethically sourced ingredients that give you the best hydration and moisturizing results, while also being mindful of the impact on our planet. " +
      "We use sustainable, clean ingredients and materials. " +
      "We created the first lip balm tube with 100% recycled plastic. ";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/planet", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/planet", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/planet", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/planet", market),
                    "name": "Planet",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="planet"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="planet"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Responsibly&nbsp;sourced.
              Sustainably&nbsp;made.
              Organically&nbsp;hydrating.
            </h1>
            <div className="flex flex-col-reverse items-center sm:flex-row">
              <div className="flex-1 my-5">
                <picture>
                  <source
                    srcSet={require("../public/images/planet/ingredients.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/planet/ingredients.png") as string}
                    type="image/png"
                  />
                  <img
                    alt="Ingredients"
                    className="block mx-auto"
                    src={require("../public/images/planet/ingredients.png") as string}
                    style={{ maxHeight: 400 }}
                  />
                </picture>
              </div>
              <div className="flex-1 my-5 text-center">
                <h2 className="font-medium font-serif text-2xl">
                  Lasting Smiles was built from the ground up &ndash; literally.
                </h2>
                <p className="text-xl">
                  We start with elements from nature, then offer them right back in ways that sustain the environment.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center sm:flex-row">
              <div className="flex-1 my-5 text-center">
                <h2 className="font-medium font-serif text-2xl">
                  Bouquet of ingredients.
                </h2>
                <p className="text-xl">
                  Each ingredient is organic and fair trade, naturally hydrating and soothing.
                  Packed with vitamins and healing nutrients to leave your lips soft, moisturized and healthy.
                  With ingredients so pure you can eat them.
                </p>
              </div>
              <div className="flex-1 my-5">
                <picture>
                  <source
                    srcSet={require("../public/images/planet/bouquet.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/planet/bouquet.png") as string}
                    type="image/png"
                  />
                  <img
                    alt="Bouquet"
                    className="block mx-auto"
                    src={require("../public/images/planet/bouquet.png") as string}
                    style={{ maxHeight: 400 }}
                  />
                </picture>
              </div>
            </div>
            <div className="flex flex-col-reverse items-center sm:flex-row">
              <div className="flex-1 my-5">
                <picture>
                  <source
                    srcSet={require("../public/images/planet/sourcing.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/planet/sourcing.png") as string}
                    type="image/png"
                  />
                  <img
                    alt="Sourcing"
                    className="block mx-auto"
                    src={require("../public/images/planet/sourcing.png") as string}
                    style={{ maxHeight: 400 }}
                  />
                </picture>
              </div>
              <div className="flex-1 my-5 text-center">
                <h2 className="font-medium font-serif text-2xl">
                  Sourced with pure integrity.
                </h2>
                <p className="text-xl">
                  Our partners include global communities that harvest the finest organic ingredients by hand &ndash; so they're always sustainably and ethically sourced.
                  While you nourish your lips with the hydrating and enriching benefits of cocoa butter, coconut oil and shea butter, feel good knowing you're providing fair trade wages to farmers and a better life for their families.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="bg-grass px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              All Eco. No Waste.
            </h2>
            <p className="text-center text-xl">
              Your reason to smile wider.
            </p>
            <div className="py-5">
              <h3 className="font-medium font-serif text-center text-xl">
                Upcycled and repurposed materials.
              </h3>
              <p className="text-center text-xl">
                Our Lasting Smiles lip balm tubes are the first to be made with 100% post-consumer recycled plastic.
                We are committed to reducing plastic waste.
              </p>
              <LazyLoad
                className="mx-auto my-5 overflow-hidden rounded"
                offset={250}
                style={{
                  maxHeight: 640,
                  maxWidth: 640,
                }}
              >
                <video
                  autoPlay={true}
                  className="mx-auto"
                  controls={true}
                  height="640"
                  loop={true}
                  muted={true}
                  width="640"
                >
                  <source
                    src={require("../public/videos/upcycled-materials.mp4")}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </LazyLoad>
            </div>
            <div className="py-5">
              <h3 className="font-medium font-serif text-center text-xl">
                Fully renewable, plant-based packaging.
              </h3>
              <p className="text-center text-xl">
                Our Lasting Smiles lip balms are packaged in 100% compostable bags made from corn and ready to be transformed back to nature's garden.
              </p>
              <LazyLoad
                className="mx-auto my-5 overflow-hidden rounded"
                offset={250}
                style={{
                  maxHeight: 640,
                  maxWidth: 640,
                }}
              >
                <video
                  autoPlay={true}
                  className="mx-auto"
                  controls={true}
                  height="640"
                  loop={true}
                  muted={true}
                  width="640"
                >
                  <source
                    src={require("../public/videos/plant-packaging.mp4")}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </LazyLoad>
            </div>
          </div>
        </section>
      </>
    );
  }
}
